class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-cart-drawer-upsell]');
      if (!form) return;
      event.preventDefault();
      this.addUpsell(form);
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  addUpsell(form) {
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[role="alert"]');
    if (!button || button.disabled) return;

    button.disabled = true;
    button.classList.add('loading');
    error.hidden = true;
    error.textContent = '';

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(form);
    formData.append('sections', this.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || window.cartStrings.error);
        const upgradeLineKey = form.dataset.upgradeLineKey;
        if (!upgradeLineKey) return response;

        const changeConfig = fetchConfig('javascript');
        changeConfig.body = JSON.stringify({
          id: upgradeLineKey,
          quantity: 0,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        });

        return fetch(routes.cart_change_url, changeConfig).then((changeResponse) => changeResponse.json());
      })
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || window.cartStrings.error);
        this.renderContents(response);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          productVariantId: formData.get('id'),
          cartData: response,
        });
      })
      .catch((exception) => {
        error.textContent = exception.message || window.cartStrings.error;
        error.hidden = false;
        button.disabled = false;
        button.classList.remove('loading');
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.animateShippingUnlock();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  animateShippingUnlock() {
    const shipping = this.querySelector('.ly-cart-drawer__shipping');
    const message = shipping?.querySelector('[data-cart-drawer-shipping-message]');
    if (!shipping || !message) return;

    shipping.classList.remove('is-unlocked');
    message.textContent = 'Free shipping available';

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => shipping.classList.add('is-unlocked'));
    });

    window.setTimeout(() => {
      if (shipping.classList.contains('is-unlocked')) message.textContent = 'Free shipping unlocked';
    }, 1200);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
